import React from "react";
import { CalendarDays, Clock3, MapPin, ArrowRight } from "lucide-react";

export type Course = {
  id: string;
  apoloScheduleId?: number;
  title: string;
  location: string;
  category: string;
  shortDescription: string;
  startDate: string;
  duration: string;
  imageUrl?: string;
  pageContent?: string;
  registrationEmail?: string;
  hidden?: boolean;
  comingSoon?: boolean;
};

export type CourseScheduleStatus = "upcoming" | "ongoing" | "finished" | "unknown";

export function getCourseScheduleInfo(value: string) {
  const matches = Array.from(value.matchAll(/(\d{1,2})\/(\d{1,2})\/(\d{4})/g));
  const toDate = (match?: RegExpMatchArray) =>
    match ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1])) : null;
  const start = toDate(matches[0]);
  const end = toDate(matches[1]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let status: CourseScheduleStatus = "unknown";
  if (end && end < today) status = "finished";
  else if (start && start > today) status = "upcoming";
  else if (start && start <= today && (!end || end >= today)) status = "ongoing";

  const enrollmentDeadline = start ? new Date(start) : null;
  enrollmentDeadline?.setDate(enrollmentDeadline.getDate() + 5);
  const startsThisMonth = Boolean(
    start && start.getFullYear() === today.getFullYear() && start.getMonth() === today.getMonth(),
  );
  const enrollmentOpen =
    (status === "upcoming" && startsThisMonth) ||
    (status === "ongoing" && Boolean(enrollmentDeadline && today <= enrollmentDeadline));
  const isUnavailable = status === "finished" || (status === "ongoing" && !enrollmentOpen);

  return { start, end, status, enrollmentDeadline, startsThisMonth, enrollmentOpen, isUnavailable };
}

export function sortCoursesByAvailability(courses: Course[]) {
  const priority = (course: Course) => {
    if (course.comingSoon) return -1;
    const schedule = getCourseScheduleInfo(course.startDate);
    if (schedule.enrollmentOpen) return 0;
    if (schedule.status === "upcoming") return 1;
    if (schedule.status === "ongoing") return 2;
    if (schedule.status === "unknown") return 3;
    return 4;
  };

  return [...courses].sort((a, b) => {
    const difference = priority(a) - priority(b);
    if (difference) return difference;
    const aSchedule = getCourseScheduleInfo(a.startDate);
    const bSchedule = getCourseScheduleInfo(b.startDate);
    if (aSchedule.status === "finished" && bSchedule.status === "finished") {
      return (bSchedule.end?.getTime() ?? 0) - (aSchedule.end?.getTime() ?? 0);
    }
    const startDifference =
      (aSchedule.start?.getTime() ?? Number.POSITIVE_INFINITY) -
      (bSchedule.start?.getTime() ?? Number.POSITIVE_INFINITY);
    return startDifference || a.title.localeCompare(b.title, "es");
  });
}

export default function CourseCard({ course, onRegister }: { course: Course; onRegister: (c: Course) => void }) {
  const { status, enrollmentOpen, isUnavailable } = getCourseScheduleInfo(course.startDate);
  const isComingSoon = Boolean(course.comingSoon);
  const statusLabel =
    isComingSoon
      ? "Próximamente"
      : enrollmentOpen
      ? "Inscripción abierta"
      : status === "finished"
      ? "Finalizado"
      : status === "ongoing" && !enrollmentOpen
        ? "No disponible"
        : status === "upcoming"
          ? "Próximamente"
          : null;
  const imageUrl = course.imageUrl
    ? encodeURI(course.imageUrl)
    : "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1";

  return (
    <article className="card group flex h-full cursor-pointer flex-col overflow-hidden p-0" onClick={() => onRegister(course)}>
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-emerald-950">
        <img
          src={imageUrl}
          alt={course.title}
          className={`h-full w-full object-cover transition duration-500 group-hover:scale-105 ${
            isUnavailable ? "grayscale blur-[1px] opacity-60" : ""
          }`}
        />
        {statusLabel && (
          <span
            className={`absolute right-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
              isUnavailable ? "bg-slate-800 text-white" : "bg-white text-emerald-950 shadow-sm"
            }`}
          >
            {statusLabel}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/80 to-transparent p-4">
          <span className="badge">{course.category}</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-xl font-semibold leading-tight text-emerald-950 transition group-hover:text-[var(--accent)]">
          {course.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-6 text-slate-600">{course.shortDescription}</p>

        <div className="mt-5 grid gap-2 border-t border-emerald-900/10 pt-4 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-[var(--accent)]" />
            <span>{course.startDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-[var(--accent)]" />
            <span>Duración: {course.duration}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[var(--accent)]" />
            <span>{course.location}</span>
          </div>
        </div>

        <button
          className="mt-5 inline-flex w-full items-center justify-between rounded-md bg-emerald-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-[var(--accent)]"
          onClick={(e) => {
            e.stopPropagation();
            onRegister(course);
          }}
        >
          {isComingSoon ? "Quiero que me avisen" : isUnavailable ? "Avísame cuando vuelva" : "Solicitar plaza"}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}
